import { useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Modal, Spinner, Text, VStack } from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";

interface Candidate { employee_Id: number; employee_Name: string; employee_Code: string; }

interface BranchGroup {
    branchId: number;
    branchName: string;
    memberIds: number[];
}

const BranchMoveCard = ({
    group,
    excludeEmployeeId,
    selected,
    onSelect,
}: {
    group: BranchGroup;
    excludeEmployeeId: number;
    selected: Candidate | null;
    onSelect: (branchId: number, candidate: Candidate | null) => void;
}) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [results, setResults] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!debounced.trim()) { setResults([]); return; }
        (async () => {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('filter', debounced);
            params.append('excludeEmployeeId', String(excludeEmployeeId));
            params.append('branchIds', String(group.branchId));

            const res = await fetchApi<Candidate[]>('TeamInteraction/teamLeadManagerSearch', 'GET', '', params.toString());
            setResults(res || []);
            setLoading(false);
        })();
    }, [debounced, group.branchId, excludeEmployeeId]);

    const noResults = !!debounced.trim() && !loading && results.length === 0;

    return (
        <Box border="1px solid #E2E8F0" borderRadius="10px" p={3}>
            <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="13px" fontWeight="700" color="#0F172A">{group.branchName}</Text>
                <Badge bg="#F0FDF4" color="#15803D" borderRadius="full" fontSize="11px" px={2}>
                    {group.memberIds.length} member(s)
                </Badge>
            </Flex>

            {selected ? (
                <Flex align="center" gap={2} bg="#F0FDF4" border="1px solid #BBF7D0" borderRadius="8px" px={3} py={2}>
                    <Box w="7px" h="7px" borderRadius="full" bg="#16A34A" />
                    <Text fontSize="13px" fontWeight="600" color="#15803D" flex={1}>{selected.employee_Name}</Text>
                    <IconButton aria-label="Clear" icon={<CloseIcon boxSize={2} />} size="xs" variant="ghost" onClick={() => onSelect(group.branchId, null)} />
                </Flex>
            ) : (
                <Box position="relative">
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none" h="34px"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input h="34px" pl={8} fontSize="13px" placeholder={`Search TL / Manager for ${group.branchName}…`} value={query} onChange={e => setQuery(e.target.value)} />
                        {loading && <Spinner size="xs" position="absolute" right={3} top="9px" />}
                    </InputGroup>

                    {noResults && (
                        <Text fontSize="11px" color="#DC2626" mt={1.5}>
                            No one has access to {group.branchName}.
                        </Text>
                    )}

                    {results.length > 0 && (
                        <Box position="absolute" top="100%" left={0} right={0} zIndex={20} bg="white" border="1px solid #E2E8F0" borderRadius="8px" boxShadow="0 4px 16px rgba(0,0,0,0.10)" maxH="180px" overflowY="auto" mt={1}>
                            {results.map(c => (
                                <Flex key={c.employee_Id} align="center" gap={2} px={3} py={2} cursor="pointer" _hover={{ bg: '#F0FDF4' }} onClick={() => { onSelect(group.branchId, c); setQuery(''); setResults([]); }}>
                                    <Text fontSize="13px" fontWeight="500">{c.employee_Name}</Text>
                                    <Text fontSize="11px" color="#94A3B8" fontFamily="monospace">{c.employee_Code}</Text>
                                </Flex>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

const MoveTeamModal = ({ isOpen, onClose, leader, onMoved }: any) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [saving, setSaving] = useState(false);
    const [selections, setSelections] = useState<Record<number, Candidate | null>>({});

    const excludeEmployeeId = leader?.employeeId ?? leader?.id;
    const teamMembers = leader?.teamMembers || [];

    // One card per distinct branch — a leader's team can span branches with
    // different eligible replacements, so each group picks its own new leader.
    const branchGroups: BranchGroup[] = useMemo(() => {
        const map = new Map<number, BranchGroup>();
        teamMembers.forEach((m: any) => {
            const branchId = m.branch_Id;
            if (branchId === null || branchId === undefined) return;
            if (!map.has(branchId)) {
                const label = m.company ? `${m.company} - ${m.branch}` : (m.branch || `Branch ${branchId}`);
                map.set(branchId, { branchId, branchName: label, memberIds: [] });
            }
            map.get(branchId)!.memberIds.push(m.employeeId ?? m.id);
        });
        return Array.from(map.values());
    }, [teamMembers]);

    useEffect(() => {
        if (isOpen) setSelections({});
    }, [isOpen]);

    const handleSelect = (branchId: number, candidate: Candidate | null) => {
        setSelections(prev => ({ ...prev, [branchId]: candidate }));
    };

    const allAssigned = branchGroups.length > 0 && branchGroups.every(g => !!selections[g.branchId]);

    const handleMove = async () => {
        if (!allAssigned) {
            addToast({ message: 'Select a new TL/Manager for every branch', status: 'error' });
            return;
        }
        setSaving(true);
        try {
            const results = await Promise.all(
                branchGroups.map(g =>
                    fetchApi('TeamInteraction/moveMembers', 'POST', {
                        member_Employee_Ids: g.memberIds,
                        new_Leader_Id: selections[g.branchId]!.employee_Id,
                        role_Type: leader?.role,
                    })
                )
            );
            if (results.every(r => r)) {
                addToast({ message: 'Team moved successfully', status: 'success' });
                onMoved?.();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent borderRadius="12px">
                <ModalHeader fontSize="15px" fontWeight="700">
                    Move {leader?.name}'s whole team ({teamMembers.length})
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="12px" color="#64748B" mb={3}>
                        {branchGroups.length > 1
                            ? `This team spans ${branchGroups.length} branches. Pick a new ${leader?.role} for each.`
                            : `All members currently reporting to ${leader?.name} as ${leader?.role} will be reassigned to the new ${leader?.role}.`}
                    </Text>

                    <VStack spacing={3} align="stretch" maxH="420px" overflowY="auto">
                        {branchGroups.map(g => (
                            <BranchMoveCard
                                key={g.branchId}
                                group={g}
                                excludeEmployeeId={excludeEmployeeId}
                                selected={selections[g.branchId] ?? null}
                                onSelect={handleSelect}
                            />
                        ))}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={saving}>Cancel</Button>
                    <Button bg="#006838" color="white" _hover={{ bg: '#005530' }} isLoading={saving} loadingText="Moving…" isDisabled={!allAssigned} onClick={handleMove}>
                        Move Team
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default MoveTeamModal;